import type { BenefitCategory, BenefitSummary, BenefitTag } from "@yutai/shared"
import { allocationFrequencyFromRecordMonths } from "../../lib/allocationFrequency.js"
import { marketCapTierFromValue } from "../../lib/marketCapTier.js"
import { nextRecordDate } from "../../lib/nextRecordDate.js"
import { relevanceLevelFromScore } from "../../lib/relevance.js"
import {
  ALLOCATION_FREQUENCY_OPTIONS,
  CAP_TIER_OPTIONS,
  FILTER_CATEGORIES,
  FILTER_MONTHS,
  FILTER_TAGS,
  HOLDING_YEAR_OPTIONS,
  MAX_ACQUISITION_AMOUNT_OPTIONS,
  MIN_BENEFIT_VALUE_OPTIONS,
  MIN_YIELD_OPTIONS,
  RELEVANCE_LEVEL_OPTIONS,
} from "./filterConstants.js"
import type { Filters, SortKey, SortOrder } from "./useBenefits.js"

export type FilterDimension =
  | "categories"
  | "tags"
  | "months"
  | "deliveryMonths"
  | "industries"
  | "minBenefitValue"
  | "longTermBonus"
  | "relevance"
  | "minDividendYield"
  | "minBenefitYield"
  | "minCombinedYield"
  | "capTiers"
  | "allocationFrequency"
  | "maxAcquisitionAmount"

export interface FilterCounts {
  categories: Record<BenefitCategory, number>
  tags: Record<BenefitTag, number>
  recordMonths: Record<number, number>
  deliveryMonths: Record<number, number>
  industries: Record<string, number>
  minBenefitValue: Record<string, number>
  longTermBonus: Record<string, number>
  relevance: Record<string, number>
  minDividendYield: Record<string, number>
  minBenefitYield: Record<string, number>
  minCombinedYield: Record<string, number>
  capTier: Record<string, number>
  allocationFrequency: Record<string, number>
  maxAcquisitionAmount: Record<string, number>
}

export function normalizeForSearch(str: string): string {
  return str
    .toLowerCase()
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
}

// 配当利回りと優待利回りは、最低必要株数を保有した場合の投資額を分母とする
// 同一基準の%なので単純合算できる（保有株数が最低必要株数を超える場合は近似が崩れる）。
export function combinedYield(item: BenefitSummary): number | null {
  if (item.dividendYield == null || item.benefitYield == null) return null
  return item.dividendYield + item.benefitYield
}

// 優待を受け取るために最低限必要な投資額（概算株価 × 最低必要株数）。
// approxStockPriceはEDINET開示データからの近似値のため、これも近似値。
export function minAcquisitionAmount(item: BenefitSummary): number | null {
  if (item.approxStockPrice == null) return null
  return item.approxStockPrice * item.minimumShares
}

// 0 = 長期保有条件なし、6 = 6年以上の条件を持つもの、それ以外はその年数の条件を持つもの
function matchesHoldingYear(item: BenefitSummary, value: number): boolean {
  const years = item.holdingYears ?? []
  if (value === 0) return years.length === 0
  if (value >= 6) return years.some((y) => y >= 6)
  return years.includes(value)
}

export function matchesFilters(
  item: BenefitSummary,
  filters: Pick<Filters, FilterDimension | "query">,
  exclude: FilterDimension[] = []
): boolean {
  const skip = new Set(exclude)

  if (filters.query) {
    const q = normalizeForSearch(filters.query)
    const matchesTier = item.tiers.some((tier) => normalizeForSearch(tier.description).includes(q))
    if (
      !normalizeForSearch(item.name).includes(q) &&
      !item.id.includes(q) &&
      !item.nameKana.includes(q) &&
      !normalizeForSearch(item.businessDescription).includes(q) &&
      !matchesTier
    ) {
      return false
    }
  }

  if (!skip.has("categories") && filters.categories.length) {
    if (!filters.categories.some((c) => item.categories.includes(c))) return false
  }

  if (!skip.has("tags") && filters.tags.length) {
    if (!filters.tags.some((t) => item.tags.includes(t))) return false
  }

  if (!skip.has("months") && filters.months.length) {
    if (!filters.months.some((m) => item.recordMonths.includes(m))) return false
  }

  if (!skip.has("deliveryMonths") && filters.deliveryMonths.length) {
    if (!filters.deliveryMonths.some((m) => item.deliveryMonths.includes(m))) return false
  }

  if (!skip.has("industries") && filters.industries.length) {
    if (!filters.industries.includes(item.industry)) return false
  }

  if (!skip.has("minBenefitValue") && filters.minBenefitValue !== null) {
    if (item.minimumBenefitValue === null || item.minimumBenefitValue < filters.minBenefitValue)
      return false
  }

  if (!skip.has("longTermBonus") && filters.longTermBonus != null) {
    if (!matchesHoldingYear(item, filters.longTermBonus)) return false
  }

  if (!skip.has("relevance") && filters.relevance.length) {
    const level = relevanceLevelFromScore(item.benefitRelevanceScore)
    if (!level || !filters.relevance.includes(level)) return false
  }

  if (!skip.has("minDividendYield") && filters.minDividendYield !== null) {
    if (item.dividendYield === null || item.dividendYield < filters.minDividendYield) return false
  }

  if (!skip.has("minBenefitYield") && filters.minBenefitYield !== null) {
    if (item.benefitYield === null || item.benefitYield < filters.minBenefitYield) return false
  }

  if (!skip.has("minCombinedYield") && filters.minCombinedYield !== null) {
    const cy = combinedYield(item)
    if (cy === null || cy < filters.minCombinedYield) return false
  }

  if (!skip.has("capTiers") && filters.capTiers.length) {
    const tier = marketCapTierFromValue(item.marketCap)
    if (!tier || !filters.capTiers.includes(tier)) return false
  }

  if (!skip.has("allocationFrequency") && filters.allocationFrequency.length) {
    const freq = allocationFrequencyFromRecordMonths(item.recordMonths)
    if (!freq || !filters.allocationFrequency.includes(freq)) return false
  }

  if (!skip.has("maxAcquisitionAmount") && filters.maxAcquisitionAmount !== null) {
    const amount = minAcquisitionAmount(item)
    if (amount === null || amount > filters.maxAcquisitionAmount) return false
  }

  return true
}

export function filterBenefits(
  data: BenefitSummary[],
  filters: Filters,
  options?: { exclude?: FilterDimension[] }
): BenefitSummary[] {
  return data.filter((item) => matchesFilters(item, filters, options?.exclude))
}

// nullは常に末尾（順序に関わらず）。両方nullなら安定ソートに委ねる。
function compareNullable(aVal: number | null, bVal: number | null, order: SortOrder): number {
  if (aVal === null && bVal === null) return 0
  if (aVal === null) return 1
  if (bVal === null) return -1
  return order === "asc" ? aVal - bVal : bVal - aVal
}

export function sortBenefits(
  data: BenefitSummary[],
  sort: SortKey,
  order: SortOrder
): BenefitSummary[] {
  return [...data].sort((a, b) => {
    switch (sort) {
      case "benefitValue":
        return compareNullable(a.minimumBenefitValue, b.minimumBenefitValue, order)
      case "combinedYield":
        return compareNullable(combinedYield(a), combinedYield(b), order)
      case "dividendYield":
        return compareNullable(a.dividendYield, b.dividendYield, order)
      case "benefitYield":
        return compareNullable(a.benefitYield, b.benefitYield, order)
      case "marketCap":
        return compareNullable(a.marketCap, b.marketCap, order)
      case "acquisitionAmount":
        return compareNullable(minAcquisitionAmount(a), minAcquisitionAmount(b), order)
      case "recordDate":
        return compareNullable(
          nextRecordDate(a.recordMonths)?.daysUntil ?? null,
          nextRecordDate(b.recordMonths)?.daysUntil ?? null,
          order
        )
      case "lastChanged": {
        const aVal = new Date(a.lastChangedAt).getTime()
        const bVal = new Date(b.lastChangedAt).getTime()
        return order === "asc" ? aVal - bVal : bVal - aVal
      }
      default:
        return order === "asc" ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id)
    }
  })
}

export function computeFilterCounts(data: BenefitSummary[], filters: Filters): FilterCounts {
  const categories = {} as Record<BenefitCategory, number>
  for (const { value } of FILTER_CATEGORIES) {
    categories[value] = data.filter(
      (item) => item.categories.includes(value) && matchesFilters(item, filters, ["categories"])
    ).length
  }

  const tags = {} as Record<BenefitTag, number>
  for (const { value } of FILTER_TAGS) {
    tags[value] = data.filter(
      (item) => item.tags.includes(value) && matchesFilters(item, filters, ["tags"])
    ).length
  }

  const recordMonths = {} as Record<number, number>
  for (const m of FILTER_MONTHS) {
    recordMonths[m] = data.filter(
      (item) => item.recordMonths.includes(m) && matchesFilters(item, filters, ["months"])
    ).length
  }

  const deliveryMonths = {} as Record<number, number>
  for (const m of FILTER_MONTHS) {
    deliveryMonths[m] = data.filter(
      (item) => item.deliveryMonths.includes(m) && matchesFilters(item, filters, ["deliveryMonths"])
    ).length
  }

  const industryValues = Array.from(new Set(data.map((item) => item.industry))).sort((a, b) =>
    a.localeCompare(b, "ja")
  )
  const industries: Record<string, number> = {}
  for (const value of industryValues) {
    industries[value] = data.filter(
      (item) => item.industry === value && matchesFilters(item, filters, ["industries"])
    ).length
  }

  const minBenefitValue: Record<string, number> = {}
  for (const { value } of MIN_BENEFIT_VALUE_OPTIONS) {
    const key = String(value)
    minBenefitValue[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minBenefitValue"])) return false
      if (value === null) return true
      return item.minimumBenefitValue !== null && item.minimumBenefitValue >= value
    }).length
  }

  const longTermBonus: Record<string, number> = {}
  for (const { value } of HOLDING_YEAR_OPTIONS) {
    longTermBonus[String(value)] = data.filter(
      (item) => matchesFilters(item, filters, ["longTermBonus"]) && matchesHoldingYear(item, value)
    ).length
  }

  const relevance: Record<string, number> = {}
  for (const { value } of RELEVANCE_LEVEL_OPTIONS) {
    relevance[value] = data.filter(
      (item) =>
        relevanceLevelFromScore(item.benefitRelevanceScore) === value &&
        matchesFilters(item, filters, ["relevance"])
    ).length
  }

  const minDividendYield: Record<string, number> = {}
  for (const { value } of MIN_YIELD_OPTIONS) {
    const key = String(value)
    minDividendYield[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minDividendYield"])) return false
      if (value === null) return true
      return item.dividendYield !== null && item.dividendYield >= value
    }).length
  }

  const minBenefitYield: Record<string, number> = {}
  for (const { value } of MIN_YIELD_OPTIONS) {
    const key = String(value)
    minBenefitYield[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minBenefitYield"])) return false
      if (value === null) return true
      return item.benefitYield !== null && item.benefitYield >= value
    }).length
  }

  const minCombinedYield: Record<string, number> = {}
  for (const { value } of MIN_YIELD_OPTIONS) {
    const key = String(value)
    minCombinedYield[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minCombinedYield"])) return false
      if (value === null) return true
      const cy = combinedYield(item)
      return cy !== null && cy >= value
    }).length
  }

  const capTier: Record<string, number> = {}
  for (const { value } of CAP_TIER_OPTIONS) {
    capTier[value] = data.filter(
      (item) =>
        marketCapTierFromValue(item.marketCap) === value &&
        matchesFilters(item, filters, ["capTiers"])
    ).length
  }

  const allocationFrequency: Record<string, number> = {}
  for (const { value } of ALLOCATION_FREQUENCY_OPTIONS) {
    allocationFrequency[value] = data.filter(
      (item) =>
        allocationFrequencyFromRecordMonths(item.recordMonths) === value &&
        matchesFilters(item, filters, ["allocationFrequency"])
    ).length
  }

  const maxAcquisitionAmount: Record<string, number> = {}
  for (const { value } of MAX_ACQUISITION_AMOUNT_OPTIONS) {
    const key = String(value)
    maxAcquisitionAmount[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["maxAcquisitionAmount"])) return false
      if (value === null) return true
      const amount = minAcquisitionAmount(item)
      return amount !== null && amount <= value
    }).length
  }

  return {
    categories,
    tags,
    recordMonths,
    deliveryMonths,
    industries,
    minBenefitValue,
    longTermBonus,
    relevance,
    minDividendYield,
    minBenefitYield,
    minCombinedYield,
    capTier,
    allocationFrequency,
    maxAcquisitionAmount,
  }
}
